// @flow
import * as React from 'react';
import { useLocalize } from '@ta/platform.i18n';

// The trips mutations which we are generating error messages for
export type TripsMutationQuery =
  | 'ADD_ITEM_COMMENT'
  | 'ADD_ITEM_TO_TRIP'
  | 'ADD_NOTE'
  | 'UPDATE_TRIP_NOTE'
  | 'CREATE_TRIP'
  | 'EDIT_PRIVACY'
  | 'EDIT_TRIP'
  | 'COPY_TRIP'
  | 'MOVE_TRIP_ITEM'
  | 'REMOVE_TRIP_ITEM'
  | 'UPDATE_COMMENT'
  | 'REMOVE_MYSAVE_ITEM'
  | 'ADD_MYSAVE_ITEM';

type TripsErrorField =
  | 'COMMENT'
  | 'DESCRIPTION'
  | 'ITEM'
  | 'NOTE_BODY'
  | 'NOTE_TITLE'
  | 'TITLE'
  | 'TRIP';

type TripsErrorType =
  | 'BLOCKED_USER'
  | 'EXCEEDS_LIMIT'
  | 'EXCEEDS_MAX_LENGTH'
  | 'FAILED_PROFANITY'
  | 'ILLEGAL_DUPLICATE'
  | 'FAILED_URL_CHECK'
  | 'INVALID_VALUE';

export type TripsError = {
  +field: ?TripsErrorField,
  +type: ?TripsErrorType,
};

export type TripsScopedError = {
  query: TripsMutationQuery,
  tripsError?: TripsError,
};

export type GetTripErrorMessageFunction = (
  query: TripsMutationQuery,
  error?: ?TripsError,
) => string;

type LocalizeTripErrorProps = {|
  query: TripsMutationQuery,
  error?: ?TripsError,
|};

/**
 * A hook that finds and translates an error message based on trip mutation/query and returned error object
 */
export const useLocalizeTripError = () => {
  const { localize } = useLocalize();

  const getTripErrorMessage: GetTripErrorMessageFunction = React.useCallback(
    (query: TripsMutationQuery, error?: ?TripsError) => {
      const type = error?.type;
      switch (query) {
        case 'ADD_ITEM_COMMENT':
          if (type === 'FAILED_PROFANITY') {
            /* Public Trips cannot contain profanity */
            return localize.string('trips_inline_profanity_check');
          } else if (type === 'FAILED_URL_CHECK') {
            /* You have entered an inappropriate URL */
            return localize.string('linkify_url_error');
          }
          /* There was a problem adding a note to this Trip item. Please try again. */
          return localize.string('trips_error_default_add_item_comment_v2');
        case 'ADD_NOTE':
          if (type === 'FAILED_PROFANITY') {
            /* Public Trips cannot contain profanity */
            return localize.string('trips_inline_profanity_check');
          }
          /* There was a problem adding a note to this Trip. Please try again. */
          return localize.string('trips_error_default_add_note');
        case 'UPDATE_TRIP_NOTE':
          /*  There was a problem updating your Trip note. Please try again. */
          return localize.string('trip_note_update_error');
        case 'UPDATE_COMMENT':
          if (type === 'FAILED_PROFANITY') {
            /* Public Trips cannot contain profanity */
            return localize.string('trips_inline_profanity_check');
          } else if (type === 'FAILED_URL_CHECK') {
            /* You have entered an inappropriate URL */
            return localize.string('linkify_url_error');
          }
          /* There was a problem updating this comment. Please try again. */
          return localize.string('trips_error_default_update_comment');
        case 'CREATE_TRIP':
          if (type === 'ILLEGAL_DUPLICATE') {
            /* You already have a Trip with this name. */
            return localize.string('trips_error_duplicate');
          } else if (type === 'FAILED_PROFANITY') {
            /* Your Trip could not be created because public Trips cannot contain profanity. Please try again. */
            return localize.string('trips_stat_modal_profanity_check_native');
          }
          /* There was a problem creating this Trip. Please try again. */
          return localize.string('create_trip_general_error_v2');
        case 'EDIT_PRIVACY':
          if (type === 'FAILED_PROFANITY') {
            /* Your Trip contains profanity and cannot be made public.
             * Please modify your Trip and try again. Questions? View TripAdvisor's
             * <a href="https://www.tripadvisorsupport.com/hc/articles/360008133913-TripAdvisor-s-Content-Policy">Content Guidelines</a>. */
            return localize.string('trip_contains_profanity');
          } else if (type === 'FAILED_URL_CHECK') {
            /* You have entered an inappropriate URL */
            return localize.string('linkify_url_error');
          }
          /* There was a problem updating the privacy of this Trip. Please try again. */
          return localize.string('trips_error_default_edit_privacy');
        case 'EDIT_TRIP':
          if (type === 'FAILED_PROFANITY') {
            /* Public Trips cannot contain profanity */
            return localize.string('trips_inline_profanity_check');
          } else if (type === 'ILLEGAL_DUPLICATE') {
            /* You already have a Trip with this name. */
            return localize.string('trips_error_duplicate');
          } else if (type === 'FAILED_URL_CHECK') {
            /* You have entered an inappropriate URL */
            return localize.string('linkify_url_error');
          }
          /* There was a problem editing this Trip. Please try again. */
          return localize.string('trips_error_default_edit_trip');
        case 'COPY_TRIP':
          if (type === 'ILLEGAL_DUPLICATE') {
            /* You already have a Trip with this name. */
            return localize.string('trips_error_duplicate');
          } else if (type === 'FAILED_PROFANITY') {
            /* Your Trip could not be created because public Trips cannot contain profanity. Please try again. */
            return localize.string('trips_stat_modal_profanity_check_native');
          } else if (type === 'INVALID_VALUE') {
            /* Please select a new name for your Trip */
            return localize.string('new_trip_name_error_msg');
          }
          /* There was a problem creating this Trip. Please try again. */
          return localize.string('create_trip_general_error_v2');
        case 'ADD_ITEM_TO_TRIP':
          if (type === 'BLOCKED_USER') {
            /* You don't have permission to take this action. */
            return localize.string('social_no_permission');
          } else if (type === 'EXCEEDS_LIMIT') {
            /* Oh no! Your Trip has too many items in it. Please remove some items and try saving again. */
            return localize.string('trips_error_save_trip_full');
          }
          /* There was a problem adding the item to this Trip. Please try again. */
          return localize.string('trips_error_default_add_item_to_trip');
        case 'MOVE_TRIP_ITEM':
          /* There was a problem moving this Trip item. Please try again. */
          return localize.string('trips_error_default_move_item');
        case 'REMOVE_TRIP_ITEM':
          /* There was a problem removing this item from your Trip. Please try again.	 */
          return localize.string('trips_error_unsave');
        case 'ADD_MYSAVE_ITEM':
          /* There was a problem with saving. Please try again. */
          return localize.string('stat_modal_general_error');
        case 'REMOVE_MYSAVE_ITEM':
          /* There was a problem removing this item. Please try again. */
          return localize.string('m1_trips_unsave_error');
        default:
          break;
      }
      /* There was a problem with this Trip. Please try again. */
      return localize.string('trips_error_general_default');
    },
    [localize],
  );

  return {
    getTripErrorMessage,
  };
};

/**
 * A simple component that delegates to {@link #useLocalizeTripError}.
 */
const LocalizeTripError = (props: LocalizeTripErrorProps) => {
  const { getTripErrorMessage } = useLocalizeTripError();
  return getTripErrorMessage(props.query, props.error);
};

export default LocalizeTripError;
